import { NgClass, NgStyle } from '@angular/common';
import {
  Component,
  EventEmitter,
  HostBinding,
  inject,
  Input,
  Output,
} from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { NavigationService } from '@core/services';
import { ADMIN_LINKS_ACTIONS, AdminLinkActionType } from '@core/types';
import { SharedDataService, ToastService } from '@shared/services';
import { Severity } from '@shared/types';
import { RouteButtonConfig } from '@shared/typescript/validators/global-shared.type';
import { transformToBoolean } from '@shared/utils/util.functions';
import { ButtonModule } from 'primeng/button';
import {
  GetButtonDropdownClassPipe,
  GetButtonFullRoutePathPipe,
} from '../../pipes/button.pipe';
import { TranslationPipe } from '../../pipes';
import { OverlayModule } from '@angular/cdk/overlay';

@Component({
  selector: 'app-shared-button',
  imports: [
    ButtonModule,
    RouterLink,
    NgStyle,
    NgClass,
    GetButtonFullRoutePathPipe,
    GetButtonDropdownClassPipe,
    TranslationPipe,
    OverlayModule,
  ],
  templateUrl: './button.component.html',
  styleUrl: './button.component.scss',
})
export class ButtonComponent {
  @Input() label = '';
  @Input() icon = '';
  @Input() src = '';

  @Input() badge = '';
  @Input() badgeClass: Severity = 'primary';

  @Input() size!: 'small' | 'large';

  @Input() loadingIcon = 'pi pi-spin pi-spinner';
  @Input() loading = false;
  @Input() showLoaderLabel = false;
  @Input() loadingLabel = '';

  @Input() severity: Severity = 'secondary';
  @Input() styleClass = '';
  @Input() height = '32px';
  @Input() minWidth = '100px';
  @Input() iconPos!: 'left' | 'right';
  @Input() iconWidth = 18;
  @Input() iconHeight = 18;
  @Input() isLastItem = false;

  /* Remarks: Now we if something is truly then no need to pass as outlined="true" just pass outlined,
   * same as with disabled, link, raised, rounded
   * TODO: Replace it will others
   */
  @Input({ transform: transformToBoolean }) text = false;
  @Input({ transform: transformToBoolean }) outlined = false;
  @Input({ transform: transformToBoolean }) disabled = false;
  @Input({ transform: transformToBoolean }) rounded = false;

  @Input() disableMouseEventDispatch = false;
  @Input() styleObject!: StyleObject;
  @Input() routerConfig: RouteButtonConfig = {
    routeLink: '',
  };

  @Input() dropdownItems: ButtonDropdownItem[] = [];
  @Input() permission?: ButtonPermissionConfig;
  /** It is used to handle force visibility */
  @Input() isForceVisibility = false;
  @Output() onDropdownItemClick = new EventEmitter();

  dropdownVisible = false;
  visibilityFlag = false;
  submenuDropdownVisible = false;
  submenuLeft = '';
  submenuTop = '';
  submenuBottom!: string;
  subDropdownMenu: ButtonDropdownItem[] = [];
  dropdownTimeout!: ReturnType<typeof setTimeout>;
  submenuTimeout!: ReturnType<typeof setTimeout>;
  private dropdownCloseTimer?: ReturnType<typeof setTimeout>;

  @Output() onClick = new EventEmitter();

  router = inject(Router);
  navigationService = inject(NavigationService);
  readonly permissionActions = ADMIN_LINKS_ACTIONS;

  constructor(
    private toastService: ToastService,
    private sharedDataService: SharedDataService,
  ) {
    this.sharedDataService.buttonLoading$.subscribe((state) => {
      this.loading = state;
    });
  }

  get badgeClassName(): string {
    return `p-badge-${this.badgeClass}`; // Dynamically construct class name
  }

  get getStyleClasses() {
    return `form-text-btn ${this.styleClass}`;
  }

  get isVisible(): boolean {
    return this.navigationService.hasLinkPermission(
      this.permission?.linkId,
      this.permission?.actions ?? [],
    );
  }

  @HostBinding('style.display')
  get hostDisplay(): string {
    return this.isVisible ? 'contents' : 'none';
  }

  handleClick(event: MouseEvent) {
    if (!this.disableMouseEventDispatch) {
      document.dispatchEvent(new MouseEvent('click'));
    }
    event.stopPropagation();
    this.onClick.emit(event);

    if (
      event.button === 0 &&
      !event.ctrlKey &&
      !event.metaKey &&
      !event.shiftKey
    ) {
      event.preventDefault();
      event.stopPropagation();
      if (this.routerConfig?.errorMessage) {
        this.toastService.openToastAsText(
          this.routerConfig?.errorMessage ?? this.routerConfig?.errorMessage,
          '',
          {
            severity: 'error',
          },
        );
        return;
      }
      if (this.routerConfig) {
        const url = this.routerConfig.getFullPath
          ? this.routerConfig?.routeLink
          : this.getFullRoutePath(this.routerConfig?.routeLink);
        if (url) {
          void this.router.navigate([url], {
            state: this.routerConfig?.state,
            queryParams: this.routerConfig?.queryParams,
          });
        }
      }
    }
  }

  toggleDropdown(visible: boolean) {
    if (this.dropdownItems && this.dropdownItems.length > 0) {
      /** Clear previous time if exists */
      if (this.dropdownTimeout) clearTimeout(this.dropdownTimeout);

      if (this.isForceVisibility) {
        /** For force visibility don't use time delay */
        this.dropdownVisible = this.visibilityFlag ? true : visible;
      } else {
        this.dropdownTimeout = setTimeout(() => {
          this.dropdownVisible = this.visibilityFlag ? true : visible;
        });
      }
    }
  }

  toggleSubmenuDropdown(
    list: ButtonDropdownItem[],
    visible: boolean,
    index = 0,
  ) {
    if (list && list.length > 0) {
      this.subDropdownMenu = list;
      if (visible) {
        const rect = document
          .querySelector(`.li${index}`)
          ?.getBoundingClientRect();
        const submenuHeight = Math.min(list.length * 36, 350); // Cap at max-height
        const viewportHeight = window.innerHeight;

        this.submenuLeft = `${rect?.width}px`;

        const spaceBelow = viewportHeight - (rect?.top || 0);
        const spaceAbove = rect?.bottom || 0;

        // Reset positioning
        this.submenuTop = 'auto';
        this.submenuBottom = 'auto';

        if (spaceBelow < submenuHeight && spaceAbove > spaceBelow) {
          // Not enough space below, position upward
          this.submenuBottom = `0px`;
          this.submenuTop = 'auto';
        } else {
          // Normal positioning
          this.submenuTop = `${(rect?.height || 0) * index}px`;
          this.submenuBottom = 'auto';
        }
      }
      if (this.submenuTimeout) clearTimeout(this.submenuTimeout);
      this.submenuTimeout = setTimeout(() => {
        this.submenuDropdownVisible = this.visibilityFlag ? true : visible;
      });
    }
  }

  getDropdownClass(index: number, item: ButtonDropdownItem) {
    return `li${index} ${item.disabled ? 'disabled' : ''}`;
  }

  getFullRoutePath(partialPath: string) {
    return partialPath ? `${this.router.url}/${partialPath}` : undefined;
  }

  handleContextMenu(event: MouseEvent, item?: ButtonDropdownItem) {
    if (this.routerConfig?.errorMessage || item?.errorMessage) {
      event.preventDefault();
      event.stopPropagation();
      this.toastService.openToastAsText(
        this.routerConfig?.errorMessage ?? item?.errorMessage,
        '',
        {
          severity: 'error',
        },
      );
    }
  }

  openDropdown(): void {
    if (this.dropdownCloseTimer) {
      clearTimeout(this.dropdownCloseTimer);
    }

    this.dropdownVisible = true;
  }

  scheduleDropdownClose(): void {
    this.dropdownCloseTimer = setTimeout(() => {
      this.dropdownVisible = false;
    }, 150);
  }

  closeDropdown(): void {
    if (this.dropdownCloseTimer) {
      clearTimeout(this.dropdownCloseTimer);
    }

    this.dropdownVisible = false;
  }
}

interface StyleObject {
  height: string;
  width: string;
}

export interface ButtonPermissionConfig {
  linkId: number | string;
  actions?: AdminLinkActionType[];
}

export interface ButtonDropdownItem<ITYpe = string | number> {
  label: string;
  selectedText?: string;
  disabled?: boolean;
  action?: () => void;
  id?: ITYpe;
  routeLink?: string;
  state?: Record<string, string>;
  queryParams?: Record<string, unknown>;
  params?: Record<string, unknown>;
  isOverride?: boolean;
  submenuList?: ButtonDropdownItem[];
  getFullPath?: boolean;
  errorMessage?: string;
  permissionKey?: string;
  permissionAction?: AdminLinkActionType[];
  src?: string;
}
